import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';

// No BAA with Google: Analytics sees route shapes only. Route params are UUIDs, numeric ids or 16+ char
// tokens, so a segment with a digit or 16+ chars becomes ':id'. Case and underscores are not a signal:
// CamelCase routes (WhatsNew) and snake_case API actions (payment_enabled) are static words.
// Query strings and hashes are dropped except standard utm_* campaign keys.
const ID_SEGMENT = /\d|^.{16,}$/;
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

const scrubPath = (path: string) =>
  path
    .split('/')
    .map((segment) => (ID_SEGMENT.test(segment) ? ':id' : segment))
    .join('/');

const scrubUrl = (href: string) => {
  const url = new URL(href, window.location.origin);
  const utm = Array.from(url.searchParams.entries()).filter(([key]) => UTM_KEYS.includes(key));
  const query = utm.length ? `?${new URLSearchParams(utm)}` : '';
  return `${url.origin}${scrubPath(url.pathname)}${query}`;
};

const titleFromScrubbedPath = (path: string) =>
  path
    .split('/')
    .filter((segment) => segment && segment !== ':id')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');

const pathToTitle = (path: string) => titleFromScrubbedPath(scrubPath(path.split(/[?#]/)[0]));

// 'set' applies to every later hit (page views, form and request events), so none carries the real URL.
// Called at GA init too: child effects (and their request events) run before GAListener's effect.
const setScrubbedPage = () =>
  ReactGA.set({
    page_location: scrubUrl(window.location.href),
    page_referrer: document.referrer ? new URL(document.referrer).origin : '',
  });

const GAListener = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();

  useEffect(() => {
    setScrubbedPage();
    const page = scrubPath(location.pathname);
    ReactGA.send({ hitType: 'pageview', page, title: titleFromScrubbedPath(page) });
  }, [location]);

  return <>{children}</>;
};

// A link-supplied GA event name (?we=) passes only in event-name shape, never as free text.
const gaEventName = (value: string | null) => (value && /^[A-Za-z][\w .-]{0,39}$/.test(value) ? value : null);

export { GAListener, gaEventName, pathToTitle, scrubUrl, setScrubbedPage };
